import React, { useEffect, useMemo, useState } from "react";
import {
    analyzeTopSalesUpload,
    applyPlanningMinPurchaseTender,
    applyPlanningPriceTender,
    applyPriceListCurrent,
    downloadUnmappedPriceListItems,
    bulkUpdatePlanningItems,
    createPlanningRun,
    createPlanningSupplierOrders,
    exportPlanningSupplierOrderExcel,
    getPlanningSupplierOrderSnimportCartSync,
    getPlanningAllocationSummary,
    getPlanningDefaults,
    getPlanningBrands,
    getPlanningSummary,
    getTopSalesSummary,
    listCurrentPrices,
    listPlanningExclusions,
    listPriceListItems,
    listPriceListUploads,
    listPlanningItems,
    listPlanningRuns,
    listPlanningSupplierOrders,
    listPurchasingSuppliers,
    listTopSalesItems,
    listTopSalesUploads,
    parsePriceListUpload,
    parseTopSalesUpload,
    previewPlanningSupplierOrderExport,
    previewPlanningPriceAllocation,
    recalculatePlanningRun,
    refreshSnimportPriceList,
    sendPlanningSupplierOrderToSnimportCart,
    updatePriceListItemMapping,
    updatePlanningItem,
    updatePurchasingSupplierSettings,
    upsertPlanningExclusion,
    uploadPriceList,
    uploadTopSales,
} from "../api/purchasingApi";
import "./PurchasingPage.css";
import {
    PLANNING_GROUPS,
    coverageDays,
    groupPlanningItems,
    isExpensive,
    isUnorderable,
    priceVsLastReceipt,
    uploadPeriodDays,
} from "./purchasingRowSignals";

const priceListFileAccept = ".xls,.xlsx,.xlsm,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12";

const parserOptions = [
    { value: "", label: "Не задан" },
    { value: "dsn_xlsx", label: "DSN xlsx" },
    { value: "biotus_xlsx", label: "Biotus xlsx" },
    { value: "vitaworld_xls", label: "Vitaworld xls" },
    { value: "medhouse_xlsx", label: "Medhouse xlsx" },
    { value: "snimport_xlsx", label: "SNIMPORT xlsx" },
];

const crumbByTab = { prices: "Прайсы и условия", top: "Топ продаж", planning: "Формирование закупки" };

const statusPillClass = (status) => {
    if (["mapped", "parsed", "analyzed", "wholesale_available", "calculated", "ready_to_confirm", "prepared", "supplier_auto", "supplier_manual", "deficit"].includes(status)) return "pill g";
    if (["dropship_only", "fulfillment_only"].includes(status)) return "pill b";
    if (["ambiguous", "parsed_with_warnings", "has_warnings", "missing_everywhere", "missing_master", "no_wholesale", "no_supplier", "no_stock", "no_price"].includes(status)) return "pill r";
    if (status === "failed" || status === "error") return "pill d";
    return "pill";
};

const Field = ({ label, children, style }) => (
    <div className="fld" style={style}><label>{label}</label>{children}</div>
);

const PurchasePriceAudit = ({ item }) => {
    const option = (item.supplier_options || []).find(value => value.is_selected && Number(value.supplier_id) === Number(item.selected_supplier_id));
    if (option?.base_price_uah == null || option.applied_discount_percent == null) return null;
    return <div className="mini" data-testid={`price-audit-${item.sku}`}>
        {formatNumber(option.base_price_uah)} − {formatNumber(Number(option.applied_discount_percent) * 100)}% = {formatNumber(option.purchase_price_uah)} грн
    </div>;
};

const CheckPill = ({ checked, onChange, children }) => (
    <div
        className={checked ? "check ck" : "check"}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => onChange(!checked)}
        onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onChange(!checked); } }}
    >
        <span className="box">{checked ? "✓" : ""}</span>{children}
    </div>
);

const apiErrorMessage = (err, fallback) => {
    if (err?.response?.status === 413) {
        return "Файл слишком большой для production nginx. Нужно увеличить client_max_body_size на сервере.";
    }
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    return err?.message || fallback;
};

const numberValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value);
};

const dateInputValue = (offsetDays = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + offsetDays);
    return date.toISOString().slice(0, 10);
};

const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    return number.toLocaleString("uk-UA", { maximumFractionDigits: 2 });
};

const formatFileSize = (size) => {
    const bytes = Number(size || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
};

const sourceSummary = (row, type) => {
    const sources = (row.sources || []).filter((source) => source.source_type === type);
    if (!sources.length) return "—";
    return sources.slice(0, 4).map((source) => {
        const name = source.source_code || source.source_name || type;
        const price = source.price_uah ? ` / ${formatNumber(source.price_uah)} грн` : "";
        return `${name}: ${formatNumber(source.stock_qty)}${price}`;
    }).join("; ");
};

const supplierOptionsSummary = (item) => {
    const options = item.supplier_options || [];
    if (!options.length) return "—";
    return options.slice(0, 4).map((option) => {
        const selected = option.is_selected ? "* " : "";
        return `${selected}${option.supplier_code}: ${formatNumber(option.stock_qty)} / ${formatNumber(option.purchase_price_uah)} грн${option.is_promo ? " (Акция)" : ""}`;
    }).join("; ");
};

const dropshipBestSummary = (item) => {
    const supplier = item.dropship_supplier_code || item.dropship_supplier_name || "дроп";
    const cost = item.dropship_min_cost_uah ? `${formatNumber(item.dropship_min_cost_uah)} грн` : "—";
    return (
        <>
            {formatNumber(item.dropship_stock_qty)}
            <div className="mini">{supplier}</div>
            <div className="mini">с/с {cost}</div>
        </>
    );
};

const planningOrderLineAmount = (item, qty) => {
    const quantity = Number(qty || 0);
    const price = Number(item.selected_purchase_price_uah || 0);
    if (Number.isNaN(quantity) || Number.isNaN(price)) return 0;
    return quantity * price;
};

const PurchasingPage = () => {
    const [activeTab, setActiveTab] = useState("prices");
    const [pricesTab, setPricesTab] = useState("cond");
    const [planningTab, setPlanningTab] = useState("matrix");
    const [suppliers, setSuppliers] = useState([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState("");
    const [supplierQuery, setSupplierQuery] = useState("");
    const [supplierActiveOnly, setSupplierActiveOnly] = useState(false);
    const [draft, setDraft] = useState({});
    const [uploads, setUploads] = useState([]);
    const [selectedUploadId, setSelectedUploadId] = useState("");
    const [items, setItems] = useState([]);
    const [currentPrices, setCurrentPrices] = useState([]);
    const [mappingStatus, setMappingStatus] = useState("");
    const [itemQuery, setItemQuery] = useState("");
    const [currentQuery, setCurrentQuery] = useState("");
    const [file, setFile] = useState(null);
    const [topSalesUploads, setTopSalesUploads] = useState([]);
    const [selectedTopSalesUploadId, setSelectedTopSalesUploadId] = useState("");
    const [topSalesItems, setTopSalesItems] = useState([]);
    const [topSalesSummary, setTopSalesSummary] = useState(null);
    const [topSalesStatus, setTopSalesStatus] = useState("");
    const [topSalesQuery, setTopSalesQuery] = useState("");
    const [topSalesFile, setTopSalesFile] = useState(null);
    const [topSalesDateFrom, setTopSalesDateFrom] = useState(dateInputValue(-13));
    const [topSalesDateTo, setTopSalesDateTo] = useState(dateInputValue(0));
    const [planningRuns, setPlanningRuns] = useState([]);
    const [selectedPlanningRunId, setSelectedPlanningRunId] = useState("");
    const [planningItems, setPlanningItems] = useState([]);
    const [planningSummary, setPlanningSummary] = useState(null);
    const [allocationSummary, setAllocationSummary] = useState(null);
    const [supplierOrders, setSupplierOrders] = useState([]);
    const [supplierOrderCartSyncs, setSupplierOrderCartSyncs] = useState({});
    const [supplierOrderWarnings, setSupplierOrderWarnings] = useState([]);
    const [supplierOrderExportWarnings, setSupplierOrderExportWarnings] = useState([]);
    const [canForceCreateOrders, setCanForceCreateOrders] = useState(false);
    const [planningStatus, setPlanningStatus] = useState("");
    const [planningQuery, setPlanningQuery] = useState("");
    const [planningSettings, setPlanningSettings] = useState({
        planning_mode: "single",
        single_supplier_id: "",
        top_sales_upload_id: "",
        sales_period_days: 30,
        target_days: 21,
        include_dropship_stock: true,
        include_fulfillment_stock: true,
        include_without_wholesale: false,
        min_sales_qty: "1",
        top_limit: "500",
        top_sales_capture_percent: "20",
        market_markup_percent: "15",
        dropship_cost_delta_percent: "0",
        rounding_mode: "ceil",
        allowed_supplier_ids: [],
        in_stock_only: true,
        brand_filters: [],
    });
    const [availableBrands, setAvailableBrands] = useState([]);
    const [brandSearch, setBrandSearch] = useState("");
    const [brandsLoading, setBrandsLoading] = useState(false);
    const [brandsError, setBrandsError] = useState("");
    const brandScopeKey = JSON.stringify((planningSettings.planning_mode === "single"
        ? [planningSettings.single_supplier_id].filter(Boolean)
        : planningSettings.allowed_supplier_ids || []).map(Number).sort((a, b) => a - b));
    useEffect(() => {
        if (activeTab !== "planning") return;
        let active = true;
        setBrandsLoading(true);
        setBrandsError("");
        getPlanningBrands(JSON.parse(brandScopeKey), Boolean(planningSettings.in_stock_only))
            .then(rows => { if (active) setAvailableBrands(rows); })
            .catch(() => { if (active) { setAvailableBrands([]); setBrandsError("Не удалось загрузить бренды. Измените поставщика или повторно откройте раздел."); } })
            .finally(() => { if (active) setBrandsLoading(false); });
        return () => { active = false; };
    }, [activeTab, brandScopeKey, planningSettings.in_stock_only]);
    const [manualQtyByPlanningItem, setManualQtyByPlanningItem] = useState({});
    const [commentByPlanningItem, setCommentByPlanningItem] = useState({});
    const [planningNameWidth, setPlanningNameWidth] = useState("360");
    const [fillSkipExpensive, setFillSkipExpensive] = useState(true);
    const [planningExclusions, setPlanningExclusions] = useState([]);
    const [exclusionDraft, setExclusionDraft] = useState({ scope_type: "sku", sku: "", supplier_code: "", comment: "" });
    const [manualSkuByItem, setManualSkuByItem] = useState({});
    const [loading, setLoading] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const selectedSupplier = useMemo(
        () => suppliers.find((item) => String(item.id) === String(selectedSupplierId)),
        [suppliers, selectedSupplierId],
    );

    const filteredSuppliers = useMemo(() => {
        const query = supplierQuery.trim().toLowerCase();
        return suppliers.filter((supplier) => {
            if (supplierActiveOnly && supplier.is_active === false) return false;
            if (!query) return true;
            return [supplier.display_name, supplier.name, supplier.code]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query));
        });
    }, [suppliers, supplierQuery, supplierActiveOnly]);

    const selectedUpload = useMemo(
        () => uploads.find((item) => String(item.id) === String(selectedUploadId)),
        [uploads, selectedUploadId],
    );

    const selectedTopSalesUpload = useMemo(
        () => topSalesUploads.find((item) => String(item.id) === String(selectedTopSalesUploadId)),
        [topSalesUploads, selectedTopSalesUploadId],
    );

    const selectedPlanningRun = useMemo(
        () => planningRuns.find((item) => String(item.id) === String(selectedPlanningRunId)),
        [planningRuns, selectedPlanningRunId],
    );

    const supplierScope = (settings) => JSON.stringify((settings.planning_mode === "single"
        ? [settings.single_supplier_id].filter(Boolean)
        : settings.allowed_supplier_ids ?? suppliers.filter(supplier => supplier.is_active !== false).map(supplier => supplier.id))
        .map(Number).sort((a, b) => a - b));
    const normalizedBrands = (values) => JSON.stringify([...new Set((values || []).map(value => value.trim().replace(/\s+/g, " ").toLowerCase()).filter(Boolean))].sort());
    const planningScopeDirty = selectedPlanningRun && (
        Boolean(planningSettings.in_stock_only) !== Boolean(selectedPlanningRun.in_stock_only)
        || normalizedBrands(planningSettings.brand_filters) !== normalizedBrands(selectedPlanningRun.brand_filters)
        || planningSettings.planning_mode !== selectedPlanningRun.planning_mode
        || supplierScope(planningSettings) !== supplierScope(selectedPlanningRun)
    );

    const planningDraftTotals = useMemo(() => {
        const initial = { qty: 0, amount: 0, hasDraft: Object.keys(manualQtyByPlanningItem).length > 0 };
        return planningItems.reduce((acc, item) => {
            const rawQty = manualQtyByPlanningItem[item.id] ?? item.manual_qty ?? "";
            const qty = Number(rawQty || 0);
            if (!Number.isNaN(qty) && qty > 0) {
                acc.qty += qty;
                acc.amount += planningOrderLineAmount(item, qty);
            }
            return acc;
        }, initial);
    }, [planningItems, manualQtyByPlanningItem]);

    const planningGroups = useMemo(() => groupPlanningItems(planningItems), [planningItems]);

    // Реальный период загрузки топа: продажи рынка «за N дн» и сверка с периодом расчёта.
    const planningTopUpload = useMemo(
        () => topSalesUploads.find((upload) => String(upload.id) === String(selectedPlanningRun?.top_sales_upload_id || "")),
        [topSalesUploads, selectedPlanningRun],
    );
    const planningTopUploadDays = uploadPeriodDays(planningTopUpload);
    const settingsTopUploadDays = uploadPeriodDays(
        topSalesUploads.find((upload) => String(upload.id) === String(planningSettings.top_sales_upload_id || "")),
    );
    // The controls prepare a new calculation. Their default must not be
    // overwritten by the currently selected historical calculation.
    const planningMode = planningSettings.planning_mode || "single";
    const planningIsSingle = planningMode === "single";
    const matrixPlanningIsSingle = selectedPlanningRun
        ? (selectedPlanningRun.planning_mode || "multi") === "single"
        : planningIsSingle;

    const loadSuppliers = async () => {
        const rows = await listPurchasingSuppliers();
        setSuppliers(rows);
        if (!selectedSupplierId && rows.length) {
            setSelectedSupplierId(String(rows[0].id));
        }
    };

    const loadUploads = async (supplierId = selectedSupplierId) => {
        if (!supplierId) {
            setUploads([]);
            return;
        }
        const rows = await listPriceListUploads({ supplier_id: supplierId });
        setUploads(rows);
        if (!selectedUploadId && rows.length) {
            setSelectedUploadId(String(rows[0].id));
        }
    };

    const loadItems = async () => {
        if (!selectedUploadId) {
            setItems([]);
            return;
        }
        const rows = await listPriceListItems(selectedUploadId, {
            mapping_status: mappingStatus || undefined,
            query: itemQuery || undefined,
            limit: 500,
        });
        setItems(rows);
    };

    const loadCurrentPrices = async () => {
        const rows = await listCurrentPrices({
            supplier_id: selectedSupplierId || undefined,
            query: currentQuery || undefined,
            limit: 500,
        });
        setCurrentPrices(rows);
    };

    const loadTopSalesUploads = async () => {
        const rows = await listTopSalesUploads({ limit: 100 });
        setTopSalesUploads(rows);
        if (!selectedTopSalesUploadId && rows.length) {
            setSelectedTopSalesUploadId(String(rows[0].id));
        }
    };

    const loadTopSalesItems = async (uploadId = selectedTopSalesUploadId) => {
        if (!uploadId) {
            setTopSalesItems([]);
            setTopSalesSummary(null);
            return;
        }
        const [rows, summary] = await Promise.all([
            listTopSalesItems(uploadId, {
                availability_status: topSalesStatus || undefined,
                query: topSalesQuery || undefined,
                limit: 500,
            }),
            getTopSalesSummary(uploadId),
        ]);
        setTopSalesItems(rows);
        setTopSalesSummary(summary);
    };

    const loadPlanningDefaults = async () => {
        const defaults = await getPlanningDefaults();
        setPlanningSettings((prev) => ({
            ...prev,
            ...defaults,
            top_sales_upload_id: defaults.top_sales_upload_id || prev.top_sales_upload_id || "",
            min_sales_qty: numberValue(defaults.min_sales_qty ?? prev.min_sales_qty),
            min_sales_amount: numberValue(defaults.min_sales_amount ?? prev.min_sales_amount),
            top_limit: numberValue(defaults.top_limit ?? prev.top_limit),
            top_sales_capture_percent: numberValue(defaults.top_sales_capture_percent ?? prev.top_sales_capture_percent),
            market_markup_percent: numberValue(defaults.market_markup_percent ?? prev.market_markup_percent),
            dropship_cost_delta_percent: numberValue(defaults.dropship_cost_delta_percent ?? prev.dropship_cost_delta_percent),
        }));
    };

    const loadPlanningRuns = async () => {
        const rows = await listPlanningRuns({ limit: 100 });
        setPlanningRuns(rows);
        if (!selectedPlanningRunId && rows.length) {
            setSelectedPlanningRunId(String(rows[0].id));
        }
    };

    const loadPlanningExclusions = async () => {
        const rows = await listPlanningExclusions();
        setPlanningExclusions(rows);
    };

    const loadPlanningItems = async (runId = selectedPlanningRunId) => {
        if (!runId) {
            setPlanningItems([]);
            setPlanningSummary(null);
            setAllocationSummary(null);
            setSupplierOrders([]);
            setSupplierOrderCartSyncs({});
            setSupplierOrderWarnings([]);
            setCanForceCreateOrders(false);
            return;
        }
        const [rows, summary, allocation, orders] = await Promise.all([
            listPlanningItems(runId, {
                status: planningStatus || undefined,
                query: planningQuery || undefined,
            }),
            getPlanningSummary(runId),
            getPlanningAllocationSummary(runId),
            listPlanningSupplierOrders(runId),
        ]);
        setPlanningItems(rows);
        setPlanningSummary(summary);
        setAllocationSummary(allocation);
        setSupplierOrders(orders);
        const snimportOrders = orders.filter((order) => ["SNIMPORT", "BELOK"].includes(String(order.supplier_code || "").toUpperCase()));
        if (snimportOrders.length) {
            const results = await Promise.all(snimportOrders.map(async (order) => [order.id, await getPlanningSupplierOrderSnimportCartSync(order.id)]));
            setSupplierOrderCartSyncs(Object.fromEntries(results.filter(([, sync]) => sync)));
        } else {
            setSupplierOrderCartSyncs({});
        }
    };

    useEffect(() => {
        loadSuppliers().catch((err) => setError(apiErrorMessage(err, "Не удалось загрузить поставщиков")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedSupplier) return;
        setDraft({
            dropship_supplier_code: selectedSupplier.dropship_supplier_code || "",
            min_purchase_amount_uah: numberValue(selectedSupplier.min_purchase_amount_uah),
            purchase_discount_percent: selectedSupplier.purchase_discount_percent == null
                ? ""
                : String(Number(selectedSupplier.purchase_discount_percent) * 100),
            purchase_currency: selectedSupplier.purchase_currency || "UAH",
            default_exchange_rate: numberValue(selectedSupplier.default_exchange_rate),
            price_list_parser_code: selectedSupplier.price_list_parser_code || "",
            notes: selectedSupplier.notes || "",
        });
        setSelectedUploadId("");
        loadUploads(selectedSupplier.id).catch((err) => setError(apiErrorMessage(err, "Не удалось загрузить историю прайсов")));
        loadCurrentPrices().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSupplierId, selectedSupplier?.id]);

    useEffect(() => {
        loadItems().catch((err) => setError(apiErrorMessage(err, "Не удалось загрузить строки прайса")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUploadId, mappingStatus]);

    useEffect(() => {
        if (activeTab !== "top") return;
        loadTopSalesUploads().catch((err) => setError(apiErrorMessage(err, "Не удалось загрузить историю топ продаж")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== "top") return;
        loadTopSalesItems().catch((err) => setError(apiErrorMessage(err, "Не удалось загрузить матрицу топ продаж")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTopSalesUploadId, topSalesStatus]);

    useEffect(() => {
        if (activeTab !== "planning") return;
        Promise.all([
            loadTopSalesUploads(),
            loadPlanningDefaults(),
            loadPlanningRuns(),
            loadPlanningExclusions(),
        ]).catch((err) => setError(apiErrorMessage(err, "Не удалось загрузить данные формирования закупки")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (!selectedPlanningRun) return;
        setPlanningSettings((prev) => ({
            ...prev,
            planning_mode: selectedPlanningRun.planning_mode || "multi",
            single_supplier_id: selectedPlanningRun.single_supplier_id || "",
            include_dropship_stock: selectedPlanningRun.include_dropship_stock,
            in_stock_only: Boolean(selectedPlanningRun.in_stock_only),
            brand_filters: selectedPlanningRun.brand_filters || [],
            market_markup_percent: numberValue(selectedPlanningRun.market_markup_percent ?? prev.market_markup_percent),
            allowed_supplier_ids: selectedPlanningRun.allowed_supplier_ids
                ?? suppliers.filter((supplier) => supplier.is_active !== false).map((supplier) => Number(supplier.id)),
        }));
    }, [selectedPlanningRun, selectedPlanningRunId, suppliers]);

    useEffect(() => {
        if (activeTab !== "planning") return;
        loadPlanningItems().catch((err) => setError(apiErrorMessage(err, "Не удалось загрузить строки формирования закупки")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlanningRunId, planningStatus]);

    const runAction = async (name, action) => {
        setLoading(name);
        setMessage("");
        setError("");
        try {
            const result = await action();
            return result;
        } catch (err) {
            setError(apiErrorMessage(err, "Операция не выполнена"));
            return null;
        } finally {
            setLoading("");
        }
    };

    const saveSettings = async () => {
        if (!selectedSupplierId) return;
        const saved = await runAction("settings", async () => updatePurchasingSupplierSettings(selectedSupplierId, {
            dropship_supplier_code: draft.dropship_supplier_code || null,
            min_purchase_amount_uah: draft.min_purchase_amount_uah || null,
            purchase_discount_percent: draft.purchase_discount_percent === "" || draft.purchase_discount_percent == null
                ? null
                : Number(draft.purchase_discount_percent) / 100,
            purchase_currency: draft.purchase_currency || "UAH",
            default_exchange_rate: draft.default_exchange_rate || null,
            price_list_parser_code: draft.price_list_parser_code || null,
            notes: draft.notes || null,
        }));
        if (saved) {
            setMessage("Настройки поставщика сохранены.");
            await loadSuppliers();
        }
    };

    const uploadFile = async () => {
        if (!selectedSupplierId || !file) {
            setError("Выберите поставщика и файл.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setMessage(`Файл ${file.name} весит ${formatFileSize(file.size)}. На production нужен повышенный nginx client_max_body_size.`);
        }
        const uploaded = await runAction("upload", async () => uploadPriceList({
            supplierId: selectedSupplierId,
            parserCode: draft.price_list_parser_code,
            file,
        }));
        if (uploaded) {
            setMessage(`Файл загружен: ${uploaded.original_filename}`);
            setSelectedUploadId(String(uploaded.id));
            setFile(null);
            await loadUploads();
        }
    };

    const parseUpload = async (uploadId = selectedUploadId) => {
        if (!uploadId) return;
        const parsed = await runAction(`parse:${uploadId}`, async () => parsePriceListUpload(uploadId));
        if (parsed) {
            setMessage(`Разбор завершен: mapped ${parsed.rows_mapped}, unmapped ${parsed.rows_unmapped}, ambiguous ${parsed.rows_ambiguous}.`);
            await loadUploads();
            await loadItems();
        }
    };

    const applyUpload = async (uploadId = selectedUploadId) => {
        if (!uploadId) return;
        const applied = await runAction(`apply:${uploadId}`, async () => applyPriceListCurrent(uploadId));
        if (applied) {
            setMessage(`Актуальный прайс обновлен: ${applied.rows_applied} строк.`);
            await loadCurrentPrices();
        }
    };

    const refreshSnimport = async () => {
        if (!selectedSupplierId) return;
        const refreshed = await runAction("snimport-refresh", async () => refreshSnimportPriceList(selectedSupplierId));
        if (!refreshed) return;
        const warnings = refreshed.warnings?.length ? ` Предупреждения: ${refreshed.warnings.join("; ")}` : "";
        setMessage(
            `SNIMPORT обновлён: применено ${refreshed.rows_applied} из ${refreshed.rows_total} строк; `
            + `акций ${refreshed.promo_applied}/${refreshed.promo_total}; `
            + `unmapped ${refreshed.rows_unmapped}, ambiguous ${refreshed.rows_ambiguous}.${warnings}`,
        );
        setSelectedUploadId(String(refreshed.upload_id));
        await Promise.all([loadUploads(), loadItems(), loadCurrentPrices()]);
    };

    const saveMapping = async (item) => {
        const sku = String(manualSkuByItem[item.id] || "").trim();
        if (!sku) {
            setError("Введите наш SKU для ручного мапинга.");
            return;
        }
        const updated = await runAction(`map:${item.id}`, async () => updatePriceListItemMapping(item.id, { sku }));
        if (updated) {
            setMessage(`Строка ${item.line_no} привязана к ${updated.mapped_sku}.`);
            await loadItems();
        }
    };

    const downloadUnmappedItems = async () => {
        if (!selectedUploadId) return;
        const file = await runAction(`unmapped-export:${selectedUploadId}`, async () => downloadUnmappedPriceListItems(selectedUploadId));
        if (!file) return;
        const url = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.download = `price_list_${selectedUploadId}_unmapped.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const uploadTopSalesFile = async () => {
        if (!topSalesFile || !topSalesDateFrom || !topSalesDateTo) {
            setError("Выберите файл и период топ продаж.");
            return;
        }
        const uploaded = await runAction("top-upload", async () => uploadTopSales({
            dateFrom: topSalesDateFrom,
            dateTo: topSalesDateTo,
            file: topSalesFile,
        }));
        if (uploaded) {
            setMessage(`Топ продаж загружен: ${uploaded.original_filename}`);
            setSelectedTopSalesUploadId(String(uploaded.id));
            setTopSalesFile(null);
            await loadTopSalesUploads();
        }
    };

    const parseTopSales = async (uploadId = selectedTopSalesUploadId) => {
        if (!uploadId) return;
        const parsed = await runAction(`top-parse:${uploadId}`, async () => parseTopSalesUpload(uploadId));
        if (parsed) {
            setMessage(`Топ продаж разобран: ${parsed.rows_valid} valid, ${parsed.rows_missing_master} missing master.`);
            await loadTopSalesUploads();
            await loadTopSalesItems(uploadId);
        }
    };

    const analyzeTopSales = async (uploadId = selectedTopSalesUploadId) => {
        if (!uploadId) return;
        const analyzed = await runAction(`top-analyze:${uploadId}`, async () => analyzeTopSalesUpload(uploadId));
        if (analyzed) {
            setMessage(`Анализ завершен: опт ${analyzed.wholesale_available}, dropship ${analyzed.dropship_only}, нет нигде ${analyzed.missing_everywhere}.`);
            await loadTopSalesUploads();
            await loadTopSalesItems(uploadId);
        }
    };

    const createPlanning = async () => {
        if (!planningSettings.top_sales_upload_id) {
            setError("Выберите проанализированный топ продаж.");
            return;
        }
        const payload = {
            planning_mode: planningSettings.planning_mode || "multi",
            single_supplier_id: planningSettings.planning_mode === "single" && planningSettings.single_supplier_id
                ? Number(planningSettings.single_supplier_id) : null,
            top_sales_upload_id: Number(planningSettings.top_sales_upload_id),
            sales_period_days: Number(planningSettings.sales_period_days || 30),
            target_days: Number(planningSettings.target_days || 21),
            include_dropship_stock: Boolean(planningSettings.include_dropship_stock),
            include_fulfillment_stock: Boolean(planningSettings.include_fulfillment_stock),
            include_without_wholesale: Boolean(planningSettings.include_without_wholesale),
            min_sales_qty: planningSettings.min_sales_qty || "0",
            min_sales_amount: planningSettings.min_sales_amount || "0",
            top_limit: planningSettings.top_limit ? Number(planningSettings.top_limit) : null,
            top_sales_capture_percent: planningSettings.top_sales_capture_percent || "100",
            market_markup_percent: planningSettings.market_markup_percent === "" ? "15" : planningSettings.market_markup_percent,
            dropship_cost_delta_percent: "0",
            rounding_mode: "ceil",
            allowed_supplier_ids: planningSettings.planning_mode === "single"
                ? (planningSettings.single_supplier_id ? [Number(planningSettings.single_supplier_id)] : [])
                : (planningSettings.allowed_supplier_ids || []).map(Number),
            in_stock_only: Boolean(planningSettings.in_stock_only),
            brand_filters: planningSettings.brand_filters || [],
        };
        const run = await runAction("planning-create", async () => createPlanningRun(payload));
        if (run) {
            setMessage(`Расчет закупки сформирован: #${run.id}, строк ${run.total_items}.`);
            setSelectedPlanningRunId(String(run.id));
            await loadPlanningRuns();
            await loadPlanningItems(run.id);
        }
    };

    const recalculatePlanning = async (runId = selectedPlanningRunId) => {
        if (!runId) return;
        const run = await runAction(
            `planning-recalc:${runId}`,
            async () => recalculatePlanningRun(runId, {
                planning_mode: planningSettings.planning_mode || "multi",
                single_supplier_id: planningSettings.planning_mode === "single" && planningSettings.single_supplier_id
                    ? Number(planningSettings.single_supplier_id) : null,
                allowed_supplier_ids: (planningSettings.allowed_supplier_ids || []).map(Number),
                include_dropship_stock: Boolean(planningSettings.include_dropship_stock),
                market_markup_percent: planningSettings.market_markup_percent === "" ? null : planningSettings.market_markup_percent,
                in_stock_only: Boolean(planningSettings.in_stock_only),
                brand_filters: planningSettings.brand_filters || [],
            }),
        );
        if (run) {
            const isNewVersion = String(run.based_on_run_id || "") === String(runId);
            setMessage(isNewVersion
                ? `Создан расчет #${run.id} по актуальным прайсам. Заказы расчета #${runId} сохранены в истории.`
                : `Расчет #${run.id} пересчитан: дефицитных ${run.deficit_items}.`);
            setSelectedPlanningRunId(String(run.id));
            await loadPlanningRuns();
            await loadPlanningItems(run.id);
        }
    };

    const savePlanningQuantities = async (runId = selectedPlanningRunId) => {
        if (!selectedPlanningRunId) return;
        const payload = {
            items: planningItems.map((item) => {
                const manualValue = manualQtyByPlanningItem[item.id] ?? item.manual_qty ?? "";
                return {
                    id: item.id,
                    manual_qty: manualValue === "" ? null : manualValue,
                    comment: commentByPlanningItem[item.id] ?? item.comment ?? "",
                };
            }),
        };
        const summary = await runAction("planning-save-qty", async () => bulkUpdatePlanningItems(runId, payload));
        if (summary) {
            setPlanningSummary(summary);
            setManualQtyByPlanningItem({});
            setCommentByPlanningItem({});
        }
        return summary;
    };

    const runPlanningAllocation = async (actionName, action, successMessage) => {
        if (!selectedPlanningRunId) return;
        if (planningScopeDirty) { setError("Настройки изменены. Пересчитайте закупку перед распределением заказа."); return; }
        const saved = await savePlanningQuantities(selectedPlanningRunId);
        if (!saved) return;
        const allocation = await runAction(actionName, async () => action(selectedPlanningRunId));
        if (allocation) {
            setAllocationSummary(allocation);
            setSupplierOrderWarnings([]);
            setSupplierOrderExportWarnings([]);
            setCanForceCreateOrders(false);
            setMessage(successMessage(allocation));
            await loadPlanningRuns();
            await loadPlanningItems();
            setAllocationSummary(allocation);
            setPlanningTab("order");
        }
    };

    const previewPlanningTender = async () => runPlanningAllocation(
        "planning-preview",
        previewPlanningPriceAllocation,
        (allocation) => `Предпросмотр готов: ${formatNumber(allocation.total_order_amount_uah)} грн.`,
    );

    const applyPriceTender = async () => runPlanningAllocation(
        "planning-price-tender",
        applyPlanningPriceTender,
        (allocation) => `Тендер по цене применен: ${formatNumber(allocation.total_order_amount_uah)} грн.`,
    );

    const applyMinPurchaseTender = async () => runPlanningAllocation(
        "planning-min-tender",
        applyPlanningMinPurchaseTender,
        (allocation) => `Тендер по минималкам применен: ${formatNumber(allocation.total_order_amount_uah)} грн.`,
    );

    const changePlanningSupplier = async (item, supplierId) => {
        const payload = { selected_supplier_id: supplierId ? Number(supplierId) : null };
        const updated = await runAction(`planning-supplier:${item.id}`, async () => updatePlanningItem(item.id, payload));
        if (updated) {
            setMessage(supplierId ? `Поставщик для ${item.sku} выбран.` : `Поставщик для ${item.sku} сброшен в auto.`);
            setSupplierOrderWarnings([]);
            setSupplierOrderExportWarnings([]);
            setCanForceCreateOrders(false);
            await loadPlanningItems();
            await loadPlanningRuns();
        }
    };

    const formPlanningOrder = async () => {
        if (!selectedPlanningRunId) return;
        if (planningScopeDirty) { setError("Настройки изменены. Пересчитайте закупку перед формированием заказа."); return; }
        const saved = await savePlanningQuantities(selectedPlanningRunId);
        if (!saved) return;
        const result = await runAction("planning-order", async () => createPlanningSupplierOrders(selectedPlanningRunId, { allow_below_min_purchase: false }));
        if (result) {
            const warnings = result.warnings || [];
            const createdAmount = (result.orders || []).reduce((sum, order) => sum + Number(order.total_amount_uah || 0), 0);
            setMessage(`Заказы сформированы: ${result.orders.length} поставщиков, ${formatNumber(createdAmount)} грн${warnings.length ? ". Есть предупреждения." : "."}`);
            setSupplierOrderWarnings(warnings);
            setSupplierOrderExportWarnings([]);
            setCanForceCreateOrders(warnings.length > 0);
            setAllocationSummary(result.allocation);
            setSupplierOrders(result.orders || []);
            setPlanningTab("order");
            await loadPlanningRuns();
            await loadPlanningItems();
        }
    };

    const fillPlanningRecommended = () => {
        if (planningRunIsFrozen) return;
        const nextManualQty = {};
        let filledCount = 0;
        let skippedExpensive = 0;
        planningItems.forEach((item) => {
            const canOrderFromSelectedSupplier = !matrixPlanningIsSingle || !isUnorderable(item);
            if (!canOrderFromSelectedSupplier) return;
            if (fillSkipExpensive && isExpensive(item)) {
                skippedExpensive += 1;
                return;
            }
            nextManualQty[item.id] = String(Math.max(Number(item.recommended_qty || 0), 0));
            filledCount += 1;
        });
        setManualQtyByPlanningItem(nextManualQty);
        setMessage(`Рекомендованные количества подставлены в ${filledCount} строк${skippedExpensive ? `, дорогих пропущено: ${skippedExpensive}` : ""}.`);
    };

    const forceFormPlanningOrder = async () => {
        if (!selectedPlanningRunId) return;
        if (planningScopeDirty) { setError("Настройки изменены. Пересчитайте закупку перед формированием заказа."); return; }
        const saved = await savePlanningQuantities(selectedPlanningRunId);
        if (!saved) return;
        const result = await runAction("planning-order-force", async () => createPlanningSupplierOrders(selectedPlanningRunId, { allow_below_min_purchase: true }));
        if (result) {
            const createdAmount = (result.orders || []).reduce((sum, order) => sum + Number(order.total_amount_uah || 0), 0);
            setMessage(`Заказы созданы с предупреждениями: ${result.orders.length} поставщиков, ${formatNumber(createdAmount)} грн.`);
            setSupplierOrderWarnings(result.warnings || []);
            setSupplierOrderExportWarnings([]);
            setCanForceCreateOrders(false);
            setAllocationSummary(result.allocation);
            setSupplierOrders(result.orders || []);
            setPlanningTab("order");
            await loadPlanningRuns();
            await loadPlanningItems();
        }
    };

    const downloadSupplierOrder = async (order) => {
        let unmatchedLines = [];
        if (order.supplier_code === "Рослина карпат") {
            const preview = await runAction(
                `planning-export-preview:${order.id}`,
                async () => previewPlanningSupplierOrderExport(order.id),
            );
            if (!preview) return;
            unmatchedLines = preview.unmatched_lines || [];
        }
        const response = await runAction(`planning-export:${order.id}`, async () => exportPlanningSupplierOrderExcel(order.id));
        if (!response) return;
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename="?([^"]+)"?/);
        link.href = blobUrl;
        link.download = match?.[1] || `purchase_order_${order.id}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        setSupplierOrderExportWarnings(unmatchedLines);
    };

    const sendSupplierOrderToSnimportCart = async (order) => {
        const sync = await runAction(`snimport-cart:${order.id}`, async () => sendPlanningSupplierOrderToSnimportCart(order.id));
        if (!sync) return;
        setSupplierOrderCartSyncs((current) => ({ ...current, [order.id]: sync }));
        const suffix = sync.skipped_lines_count ? ` Пропущено: ${sync.skipped_lines_count}.` : "";
        setMessage(`Корзина SNIMPORT обновлена: добавлено ${sync.added_lines_count} из ${sync.requested_lines_count} строк, ${formatNumber(sync.added_qty)} шт.${suffix}`);
    };

    const focusPlanningManualInput = (currentIndex, direction) => {
        const inputs = Array.from(document.querySelectorAll("[data-planning-manual-index]"));
        const target = inputs.find((input) => Number(input.getAttribute("data-planning-manual-index")) === currentIndex + direction);
        if (target) {
            target.focus();
            target.select?.();
        }
    };

    const savePlanningExclusion = async () => {
        const payload = {
            scope_type: exclusionDraft.scope_type,
            sku: exclusionDraft.scope_type === "sku" ? exclusionDraft.sku : null,
            supplier_code: exclusionDraft.scope_type === "supplier" ? exclusionDraft.supplier_code : null,
            is_active: true,
            comment: exclusionDraft.comment || null,
        };
        const saved = await runAction("planning-exclusion", async () => upsertPlanningExclusion(payload));
        if (saved) {
            setMessage("Исключение сохранено. Пересчитайте активный расчет, чтобы применить его.");
            setExclusionDraft({ scope_type: "sku", sku: "", supplier_code: "", comment: "" });
            await loadPlanningExclusions();
        }
    };

    const toggleExclusion = async (exclusion, nextActive) => {
        const payload = {
            scope_type: exclusion.scope_type,
            sku: exclusion.scope_type === "sku" ? exclusion.sku : null,
            supplier_id: exclusion.scope_type === "supplier" ? exclusion.supplier_id : null,
            supplier_code: exclusion.scope_type === "supplier" ? exclusion.supplier_code : null,
            is_active: nextActive,
            comment: exclusion.comment || null,
        };
        const saved = await runAction(`planning-exclusion-toggle:${exclusion.id}`, async () => upsertPlanningExclusion(payload));
        if (saved) {
            setMessage(nextActive ? "Исключение включено." : "Исключение выключено.");
            await loadPlanningExclusions();
        }
    };

    const matrixColSpan = matrixPlanningIsSingle ? 9 : 10;

    const renderMatrixRow = (item, index) => {
        const expensive = isExpensive(item);
        const unorderable = isUnorderable(item);
        const rowClasses = [expensive ? "row-expensive" : "", unorderable ? "row-unorderable" : ""].filter(Boolean).join(" ");
        const periodDays = Number(selectedPlanningRun?.sales_period_days || 1);
        const lastReceiptTrend = priceVsLastReceipt(item);
        const salesPeriod = selectedPlanningRun?.sales_period_from && selectedPlanningRun?.sales_period_to
            ? `${selectedPlanningRun.sales_period_from.split("-").reverse().join(".")}–${selectedPlanningRun.sales_period_to.split("-").reverse().join(".")}`
            : `${periodDays} дн; точные даты доступны после пересчёта`;
        const salesTitle = `Период по дате создания заказа: ${salesPeriod}. Склад — завершённые продажи и незавершённые заказы с поставщиком собственного склада; ниже указаны дни наличия и с/с. Общий — завершённые продажи и незавершённые заказы всех источников, включая неизвестные. Отмены, возвраты и удалённые заказы исключены.`;
        return (
        <tr
            key={item.id}
            className={rowClasses || undefined}
            data-testid={`planning-row-${item.sku}`}
            title={expensive
                ? `Закупка ${formatNumber(item.selected_purchase_price_uah)} грн дороже с/с рынка ${formatNumber(item.top_sales_normalized_cost_uah)} грн`
                : undefined}
        >
            <td>
                {item.sku}
                {item.top_sales_rank ? <span className="top-rank" title="Место в топе продаж">#{item.top_sales_rank}</span> : null}
                <div><span className={statusPillClass(item.priority_status)}>{item.priority_status}</span></div>
                <div style={{ marginTop: "3px" }}><span className={statusPillClass(item.supplier_selection_status)}>{item.supplier_selection_status}</span></div>
            </td>
            <td style={{ width: `${planningNameWidth}px`, whiteSpace: "normal", overflowWrap: "anywhere" }}>
                {item.product_name || "—"}<div className="mini">{item.manufacturer || ""}</div>
            </td>
            <td>
                <strong>{formatNumber(item.own_warehouse_stock_qty)} / {formatNumber(item.fulfillment_stock_qty)}</strong>
                <div className="mini">свой / фулфилмент</div>
                {Number(item.own_warehouse_stock_qty || 0) > 0 ? <div className="mini">с/с {formatNumber(item.own_warehouse_cost_uah)} грн</div> : null}
                {Number(item.own_warehouse_stock_qty || 0) > 0 && lastReceiptTrend ? (
                    <div
                        className={lastReceiptTrend === "up" ? "mini trend-up" : "mini trend-down"}
                        title={`Прошлая закупка ${formatNumber(item.own_warehouse_last_cost_uah)} грн`}
                    >
                        {lastReceiptTrend === "up" ? "↑ дороже прошлой" : "↓ дешевле прошлой"}
                    </div>
                ) : null}
            </td>
            <td className="sales-metric" title={salesTitle}>
                <strong>{item.own_warehouse_ordered_qty == null ? "—" : formatNumber(item.own_warehouse_ordered_qty)} / {formatNumber(item.own_ordered_qty)}</strong>
                {item.own_warehouse_ordered_qty == null ? <div className="mini">Пересчитайте расчёт</div> : null}
                <div className="mini">{formatNumber(item.own_stock_available_days)} дн · с/с {formatNumber(item.own_sales_cost_uah)} грн</div>
            </td>
            <td className="sales-metric">
                <strong>{formatNumber(item.forecast_sales_qty)}</strong>
                <div className="mini">рынок {formatNumber(item.sales_period_qty)} шт / {planningTopUploadDays || selectedPlanningRun?.sales_period_days || "—"} дн</div>
                <div className="mini">наша доля {formatNumber(selectedPlanningRun?.top_sales_capture_percent)}%</div>
                <div className={expensive ? "mini market-cost-warn" : "mini"}>с/с {formatNumber(item.top_sales_normalized_cost_uah)} грн</div>
            </td>
            <td>
                <input
                    className="inp inp-sm qty"
                    data-planning-manual-index={index}
                    value={manualQtyByPlanningItem[item.id] ?? numberValue(item.manual_qty)}
                    placeholder={formatNumber(item.recommended_qty)}
                    aria-label={`Заказать ${item.product_name || item.sku}`}
                    onChange={(e) => setManualQtyByPlanningItem({ ...manualQtyByPlanningItem, [item.id]: e.target.value })}
                    disabled={planningRunIsFrozen || (matrixPlanningIsSingle && unorderable)}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown") { e.preventDefault(); focusPlanningManualInput(index, 1); }
                        if (e.key === "ArrowUp") { e.preventDefault(); focusPlanningManualInput(index, -1); }
                    }}
                />
            </td>
            <td className="coverage-metric">
                {(() => {
                    const days = coverageDays(item, manualQtyByPlanningItem[item.id] ?? item.manual_qty ?? 0);
                    return days === null ? "—" : `${formatNumber(days)} дн`;
                })()}
                <div className="mini">склад + фулфилмент + заказ</div>
            </td>
            {matrixPlanningIsSingle ? (
                <td>
                    <strong className={expensive ? "price-warn" : undefined}>{formatNumber(item.selected_purchase_price_uah)} грн</strong>
                    <PurchasePriceAudit item={item} />
                    <div className="mini">
                        {Number(item.selected_supplier_stock_qty || 0) > 0
                            ? `Остаток: ${formatNumber(item.selected_supplier_stock_qty)}`
                            : "Нет остатка у поставщика"}
                    </div>
                </td>
            ) : (
            <td>
                <select
                    className="inp inp-sm supsel"
                    value={item.selected_supplier_id || ""}
                    onChange={(e) => changePlanningSupplier(item, e.target.value)}
                    disabled={planningRunIsFrozen || loading === `planning-supplier:${item.id}`}
                >
                    <option value="">auto / не выбран</option>
                    {(item.supplier_options || []).map((option) => (
                        <option key={option.id} value={option.supplier_id}>
                            {option.supplier_code} / {formatNumber(option.purchase_price_uah)} грн / {formatNumber(option.stock_qty)}
                        </option>
                    ))}
                </select>
                <PurchasePriceAudit item={item} />
            </td>
            )}
            {matrixPlanningIsSingle ? <td>{dropshipBestSummary(item)}</td> : null}
            {!matrixPlanningIsSingle ? <td>{dropshipBestSummary(item)}</td> : null}
            {!matrixPlanningIsSingle ? <td>{supplierOptionsSummary(item)}</td> : null}
        </tr>
        );
    };

    const renderMatrixBody = () => {
        if (!planningItems.length) {
            return <tr><td colSpan={matrixColSpan}>Сформируйте расчет закупки.</td></tr>;
        }
        const rows = [];
        let index = 0;
        PLANNING_GROUPS.forEach((group) => {
            const groupItems = planningGroups[group.key];
            if (!groupItems.length) return;
            rows.push(<tr key={`grp-${group.key}`} className={group.className}><td colSpan={matrixColSpan}>{group.title} · {groupItems.length}</td></tr>);
            groupItems.forEach((item) => { rows.push(renderMatrixRow(item, index)); index += 1; });
        });
        return rows;
    };

    const orderHasResult = Boolean(allocationSummary) || supplierOrders.length > 0;
    const planningRunIsFrozen = selectedPlanningRun?.status === "confirmed";
    const supplierAvatar = (selectedSupplier?.display_name || selectedSupplier?.name || selectedSupplier?.code || "?").trim().charAt(0).toUpperCase();
    const isSnimportSupplier = ["SNIMPORT", "BELOK"].includes(String(selectedSupplier?.code || "").toUpperCase());

    return (
        <div className="purchasing-page">
            <div className="pp-head">
                <h1>Закупки</h1>
                <span className="crumb">{crumbByTab[activeTab]}</span>
            </div>

            <div className="subtabs">
                {[
                    ["prices", "Прайсы и условия"],
                    ["top", "Топ продаж"],
                    ["planning", "Формирование закупки"],
                ].map(([key, label]) => (
                    <button key={key} type="button" className={activeTab === key ? "subtab active" : "subtab"} onClick={() => setActiveTab(key)}>
                        {label}
                    </button>
                ))}
            </div>

            {message ? <div className="msg-ok">{message}</div> : null}
            {error ? <div className="msg-err">{error}</div> : null}

            {/* ===================== ПРАЙСЫ И УСЛОВИЯ ===================== */}
            {activeTab === "prices" ? (
                <div className="sub active">
                    <div className="split">
                        <aside className="card picker">
                            <h2>Поставщики опт</h2>
                            <div className="search">
                                <input className="inp inp-sm" value={supplierQuery} onChange={(e) => setSupplierQuery(e.target.value)} placeholder="Поиск по коду или названию…" />
                            </div>
                            <div className="picker-actions">
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => loadSuppliers().catch(() => {})}>Обновить</button>
                            </div>
                            <div className="switch-row" onClick={() => setSupplierActiveOnly(!supplierActiveOnly)}>
                                <span className={supplierActiveOnly ? "switch" : "switch off"}></span>Только активные
                            </div>
                            <div className="sup-list">
                                {filteredSuppliers.map((supplier) => (
                                    <button
                                        key={supplier.id}
                                        type="button"
                                        className={String(supplier.id) === String(selectedSupplierId) ? "sup-item sel" : "sup-item"}
                                        onClick={() => setSelectedSupplierId(String(supplier.id))}
                                    >
                                        <span className="nm">{supplier.display_name || supplier.name} <span className="mini">({supplier.code})</span></span>
                                        <span className="meta">parser {supplier.price_list_parser_code || "не задан"} · drop {supplier.dropship_supplier_code || "—"}</span>
                                    </button>
                                ))}
                                {!filteredSuppliers.length ? <div className="mini">Поставщики не найдены.</div> : null}
                            </div>
                        </aside>

                        <section className="detail">
                            <div className="card detail-head">
                                <div className="avatar">{supplierAvatar}</div>
                                <div className="who">
                                    <span className="t">{selectedSupplier?.display_name || selectedSupplier?.name || "Поставщик не выбран"} {selectedSupplier ? <span className="mini" style={{ fontWeight: 600 }}>({selectedSupplier.code})</span> : null}</span>
                                    <span className="s">
                                        <span className="pill b">{selectedSupplier?.price_list_parser_code || "parser не задан"}</span>
                                        <span>drop {selectedSupplier?.dropship_supplier_code || "—"}</span>
                                        <span>·</span>
                                        <span>{selectedSupplier?.purchase_currency || "UAH"}</span>
                                    </span>
                                </div>
                                <div className="acts">
                                    <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={!selectedSupplierId || loading === "settings"}>{loading === "settings" ? "Сохранение..." : "Сохранить"}</button>
                                </div>
                            </div>

                            <div className="card" style={{ padding: "14px 14px 4px" }}>
                                <div className="tabbar" role="tablist">
                                    {[
                                        ["cond", "★ Условия"],
                                        ["upload", "Загрузка прайса"],
                                        ["rows", `Строки прайса${selectedUpload ? ` #${selectedUpload.id}` : ""}`],
                                        ["current", "Актуальный срез"],
                                    ].map(([key, label]) => (
                                        <button key={key} type="button" className={pricesTab === key ? "tab active" : "tab"} onClick={() => setPricesTab(key)}>{label}</button>
                                    ))}
                                </div>

                                {pricesTab === "cond" ? (
                                    <div className="panel">
                                        <div className="block">
                                            <div className="bh"><span className="ic">₴</span><h4>Условия закупки</h4><span className="hint">действуют для всех прайсов поставщика</span></div>
                                            <div className="grid3">
                                                <Field label="Связанный dropship код">
                                                    <input className="inp" value={draft.dropship_supplier_code || ""} onChange={(e) => setDraft({ ...draft, dropship_supplier_code: e.target.value })} placeholder="D1 / D2 / D14" />
                                                </Field>
                                                <Field label="Мин. закупка, грн">
                                                    <input className="inp" value={draft.min_purchase_amount_uah || ""} onChange={(e) => setDraft({ ...draft, min_purchase_amount_uah: e.target.value })} placeholder="10000" />
                                                </Field>
                                                <Field label="Скидка от прайса, %">
                                                    <input className="inp" type="number" min="0" max="100" step="0.01" value={draft.purchase_discount_percent || ""} onChange={(e) => setDraft({ ...draft, purchase_discount_percent: e.target.value })} placeholder={isSnimportSupplier ? "7" : "0"} />
                                                </Field>
                                                <Field label="Валюта закупки">
                                                    <select className="inp" value={draft.purchase_currency || "UAH"} onChange={(e) => setDraft({ ...draft, purchase_currency: e.target.value })}>
                                                        <option value="UAH">UAH</option>
                                                        <option value="USD">USD</option>
                                                    </select>
                                                </Field>
                                                <Field label="Fallback курс">
                                                    <input className="inp" value={draft.default_exchange_rate || ""} onChange={(e) => setDraft({ ...draft, default_exchange_rate: e.target.value })} placeholder="45.4" />
                                                </Field>
                                                <Field label="Parser profile">
                                                    <select className="inp" value={draft.price_list_parser_code || ""} onChange={(e) => setDraft({ ...draft, price_list_parser_code: e.target.value })}>
                                                        {parserOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                                    </select>
                                                </Field>
                                            </div>
                                            <div className="fld" style={{ marginTop: "12px" }}>
                                                <label>Заметки</label>
                                                <textarea className="inp" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                                            </div>
                                            <div className="save-bar">
                                                <span className="note">Изменения применяются к выбранному поставщику</span>
                                                <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={loading === "settings"}>{loading === "settings" ? "Сохранение..." : "Сохранить условия"}</button>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {pricesTab === "upload" ? (
                                    <div className="panel">
                                        <div className="block">
                                            <div className="bh"><span className="ic">⬆</span><h4>Загрузка прайса</h4></div>
                                            {isSnimportSupplier ? (
                                                <div style={{ marginBottom: "12px", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                                                    <button type="button" className="btn btn-primary" onClick={refreshSnimport} disabled={loading === "snimport-refresh"}>
                                                        {loading === "snimport-refresh" ? "Загружаю, объединяю и применяю…" : "Обновить прайс SNIMPORT"}
                                                    </button>
                                                    <span className="mini">Скачивание прайса и акций, разбор и применение одним действием</span>
                                                </div>
                                            ) : null}
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "12px", alignItems: "end" }}>
                                                <Field label="Excel файл">
                                                    <input className="inp" type="file" accept={priceListFileAccept} onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                                </Field>
                                                <button type="button" className="btn btn-primary" onClick={uploadFile} disabled={loading === "upload"}>{loading === "upload" ? "Загрузка..." : "Загрузить"}</button>
                                                <button type="button" className="btn btn-ghost" onClick={() => loadUploads()}>Обновить историю</button>
                                            </div>
                                        </div>
                                        <div className="block">
                                            <div className="bh"><span className="ic">☰</span><h4>История загрузок</h4></div>
                                            <div className="tbl-wrap">
                                                <table className="tbl">
                                                    <thead><tr><th>Файл</th><th>Статус</th><th>Строки</th><th>Действия</th></tr></thead>
                                                    <tbody>
                                                        {uploads.map((upload) => (
                                                            <tr key={upload.id} style={{ background: String(upload.id) === String(selectedUploadId) ? "var(--sp-accent-soft)" : undefined }}>
                                                                <td>
                                                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedUploadId(String(upload.id))}>{upload.original_filename}</button>
                                                                    <div className="mini">{upload.parser_code}</div>
                                                                </td>
                                                                <td><span className={statusPillClass(upload.status)}>{upload.status}</span></td>
                                                                <td>{upload.rows_total} / {upload.rows_mapped} mapped</td>
                                                                <td>
                                                                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => parseUpload(upload.id)} disabled={loading === `parse:${upload.id}`}>Разобрать</button>
                                                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => applyUpload(upload.id)} disabled={loading === `apply:${upload.id}`}>Применить</button>
                                                                        {selectedSupplier?.dropship_supplier_code === "D16" && upload.parser_code === "vitaworld_xls" ? <span className="mini">Архивный прайс: себестоимость D16 берётся из feed.</span> : null}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {!uploads.length ? <tr><td colSpan={4}>Загрузок пока нет.</td></tr> : null}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {pricesTab === "rows" ? (
                                    <div className="panel">
                                        <div className="toolbar">
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={downloadUnmappedItems} disabled={!selectedUploadId || loading === `unmapped-export:${selectedUploadId}`}>
                                                {loading === `unmapped-export:${selectedUploadId}` ? "Формирую Excel…" : "Скачать несопоставленные Excel"}
                                            </button>
                                            <select className="inp inp-sm" value={mappingStatus} onChange={(e) => setMappingStatus(e.target.value)}>
                                                <option value="">Все статусы</option>
                                                <option value="mapped">Mapped</option>
                                                <option value="unmapped">Unmapped</option>
                                                <option value="ambiguous">Ambiguous</option>
                                                <option value="error">Error</option>
                                            </select>
                                            <input className="inp inp-sm" value={itemQuery} onChange={(e) => setItemQuery(e.target.value)} placeholder="SKU, код поставщика, barcode, название" />
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={loadItems}>Фильтр</button>
                                        </div>
                                        <div className="tbl-wrap" style={{ maxHeight: "560px" }}>
                                            <table className="tbl">
                                                <thead>
                                                    <tr>
                                                        <th>Наш код</th><th>Наше название</th><th>Поставщик</th><th>Код/ШК</th><th>Кол-во</th><th>Цена заказа</th><th>РРЦ</th><th>Статус</th><th>Ручной SKU</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((item) => (
                                                        <tr key={item.id}>
                                                            <td>{item.mapped_sku || "—"}</td>
                                                            <td>{item.mapped_name_ua || "—"}</td>
                                                            <td>{item.supplier_product_name || "—"}{item.is_promo ? <div><span className="pill b">Акция</span></div> : null}</td>
                                                            <td>{item.supplier_product_code || "—"}<div className="mini">{item.barcode || ""}</div></td>
                                                            <td>{item.stock_qty}</td>
                                                            <td>{item.order_price_uah || "—"}</td>
                                                            <td>{item.rrp_uah || "—"}</td>
                                                            <td><span className={statusPillClass(item.mapping_status)}>{item.mapping_status}</span><div className="mini">{item.mapping_source || item.mapping_error || ""}</div></td>
                                                            <td>
                                                                <div style={{ display: "flex", gap: "6px" }}>
                                                                    <input className="inp inp-sm" style={{ minWidth: "130px" }} value={manualSkuByItem[item.id] || ""} onChange={(e) => setManualSkuByItem({ ...manualSkuByItem, [item.id]: e.target.value })} placeholder="наш SKU" />
                                                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => saveMapping(item)} disabled={loading === `map:${item.id}`}>OK</button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {!items.length ? <tr><td colSpan={9}>Выберите разобранную загрузку.</td></tr> : null}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : null}

                                {pricesTab === "current" ? (
                                    <div className="panel">
                                        <div className="toolbar narrow">
                                            <input className="inp inp-sm" value={currentQuery} onChange={(e) => setCurrentQuery(e.target.value)} placeholder="Наш SKU, barcode, название, код поставщика" />
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={loadCurrentPrices}>Фильтр</button>
                                        </div>
                                        <div className="tbl-wrap" style={{ maxHeight: "520px" }}>
                                            <table className="tbl">
                                                <thead><tr><th>Наш код</th><th>Наше название</th><th>Поставщик</th><th>Код поставщика</th><th>Остаток</th><th>Цена</th><th>РРЦ</th><th>Источник</th></tr></thead>
                                                <tbody>
                                                    {currentPrices.map((row) => (
                                                        <tr key={row.id}>
                                                            <td>{row.mapped_sku}</td>
                                                            <td>{row.mapped_name_ua || "—"}</td>
                                                            <td>{row.supplier_name || row.supplier_code}</td>
                                                            <td>{row.supplier_product_code}<div className="mini">{row.barcode || ""}</div></td>
                                                            <td>{row.stock_qty}</td>
                                                            <td>{row.order_price_uah}{row.is_promo ? <div><span className="pill b">Акция</span></div> : null}</td>
                                                            <td>{row.rrp_uah || "—"}</td>
                                                            <td>{row.mapping_source || "—"}</td>
                                                        </tr>
                                                    ))}
                                                    {!currentPrices.length ? <tr><td colSpan={8}>Актуальный срез пуст.</td></tr> : null}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </section>
                    </div>
                </div>
            ) : null}

            {/* ===================== ТОП ПРОДАЖ ===================== */}
            {activeTab === "top" ? (
                <div className="sub active">
                    <div className="block">
                        <div className="bh"><span className="ic">⬆</span><h4>Загрузка топ продаж</h4></div>
                        <div style={{ display: "grid", gridTemplateColumns: "160px 160px 1fr auto auto", gap: "12px", alignItems: "end" }}>
                            <Field label="Дата с"><input className="inp" type="date" value={topSalesDateFrom} onChange={(e) => setTopSalesDateFrom(e.target.value)} /></Field>
                            <Field label="Дата по"><input className="inp" type="date" value={topSalesDateTo} onChange={(e) => setTopSalesDateTo(e.target.value)} /></Field>
                            <Field label="Excel файл"><input className="inp" type="file" accept=".xlsx,.xlsm" onChange={(e) => setTopSalesFile(e.target.files?.[0] || null)} /></Field>
                            <button type="button" className="btn btn-primary" onClick={uploadTopSalesFile} disabled={loading === "top-upload"}>{loading === "top-upload" ? "Загрузка..." : "Загрузить"}</button>
                            <button type="button" className="btn btn-ghost" onClick={loadTopSalesUploads}>Обновить историю</button>
                        </div>
                    </div>

                    <div className="block">
                        <div className="bh"><span className="ic">☰</span><h4>История топ продаж</h4></div>
                        <div className="tbl-wrap">
                            <table className="tbl">
                                <thead><tr><th>Файл</th><th>Период</th><th>Статус</th><th>SKU</th><th>Missing master</th><th>Действия</th></tr></thead>
                                <tbody>
                                    {topSalesUploads.map((upload) => (
                                        <tr key={upload.id} style={{ background: String(upload.id) === String(selectedTopSalesUploadId) ? "var(--sp-accent-soft)" : undefined }}>
                                            <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedTopSalesUploadId(String(upload.id))}>{upload.original_filename}</button></td>
                                            <td>{upload.date_from} - {upload.date_to}</td>
                                            <td><span className={statusPillClass(upload.status)}>{upload.status}</span></td>
                                            <td>{upload.rows_total} / {upload.rows_valid} valid</td>
                                            <td>{upload.rows_missing_master}</td>
                                            <td>
                                                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => parseTopSales(upload.id)} disabled={loading === `top-parse:${upload.id}`}>Разобрать</button>
                                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => analyzeTopSales(upload.id)} disabled={loading === `top-analyze:${upload.id}`}>Анализировать</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {!topSalesUploads.length ? <tr><td colSpan={6}>Загрузок пока нет.</td></tr> : null}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="strip">
                        {[
                            ["Всего SKU", topSalesSummary?.rows_total, ""],
                            ["Есть в опте", topSalesSummary?.wholesale_available, ""],
                            ["Только dropship", topSalesSummary?.dropship_only, ""],
                            ["Только fulfillment", topSalesSummary?.fulfillment_only, ""],
                            ["Нет нигде", topSalesSummary?.missing_everywhere, "def"],
                            ["Missing master", topSalesSummary?.missing_master, ""],
                        ].map(([label, value, mod]) => (
                            <div key={label} className={mod ? `stat ${mod}` : "stat"}>
                                <div className="k">{label}</div>
                                <div className="v">{formatNumber(value || 0)}</div>
                            </div>
                        ))}
                    </div>

                    <div className="block">
                        <div className="bh"><span className="ic">▦</span><h4>Матрица топ продаж {selectedTopSalesUpload ? `#${selectedTopSalesUpload.id}` : ""}</h4></div>
                        <div className="toolbar">
                            <select className="inp inp-sm" value={topSalesStatus} onChange={(e) => setTopSalesStatus(e.target.value)}>
                                <option value="">Все статусы</option>
                                <option value="wholesale_available">Есть в опте</option>
                                <option value="dropship_only">Только dropship</option>
                                <option value="fulfillment_only">Только fulfillment</option>
                                <option value="missing_everywhere">Нет нигде</option>
                                <option value="missing_master">Missing master</option>
                                <option value="not_analyzed">Не анализировалось</option>
                            </select>
                            <input className="inp inp-sm" value={topSalesQuery} onChange={(e) => setTopSalesQuery(e.target.value)} placeholder="SKU, название, производитель" />
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => loadTopSalesItems()}>Фильтр</button>
                        </div>
                        <div className="tbl-wrap" style={{ maxHeight: "620px" }}>
                            <table className="tbl">
                                <thead>
                                    <tr>
                                        <th>SKU</th><th>Название</th><th>Производитель</th><th>Сумма</th><th>Кол-во</th><th>Статус</th><th>Опт</th><th>Dropship</th><th>Fulfillment</th><th>Рекомендация</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topSalesItems.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.sku || item.raw_sku}</td>
                                            <td>{item.master_name_ua || item.source_name_ua || "—"}</td>
                                            <td>{item.master_manufacturer || item.source_manufacturer || "—"}</td>
                                            <td>{formatNumber(item.sales_amount)}</td>
                                            <td>{formatNumber(item.sales_qty)}</td>
                                            <td><span className={statusPillClass(item.availability_status)}>{item.availability_status}</span></td>
                                            <td>
                                                {formatNumber(item.wholesale_stock_qty)}
                                                <div className="mini">{item.wholesale_min_price_uah ? `${formatNumber(item.wholesale_min_price_uah)} грн` : "—"}</div>
                                                <div className="mini">{sourceSummary(item, "wholesale")}</div>
                                            </td>
                                            <td>
                                                {formatNumber(item.dropship_stock_qty)}
                                                <div className="mini">{sourceSummary(item, "dropship")}</div>
                                            </td>
                                            <td>
                                                {formatNumber(item.fulfillment_stock_qty)}
                                                <div className="mini">{sourceSummary(item, "fulfillment")}</div>
                                            </td>
                                            <td>{item.recommendation_reason || "—"}</td>
                                        </tr>
                                    ))}
                                    {!topSalesItems.length ? <tr><td colSpan={10}>Выберите и разберите загрузку топ продаж.</td></tr> : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* ===================== ФОРМИРОВАНИЕ ЗАКУПКИ ===================== */}
            {activeTab === "planning" ? (
                <div className="sub active">
                    <div className="card" style={{ padding: "14px 14px 4px" }}>
                        <div className="tabbar" role="tablist">
                            <button type="button" className={planningTab === "matrix" ? "tab active" : "tab"} onClick={() => setPlanningTab("matrix")}><span className="star">★</span>Матрица</button>
                            <button type="button" className={planningTab === "order" ? "tab active" : "tab"} onClick={() => setPlanningTab("order")}>Заказ <span className="count">{supplierOrders.length}</span></button>
                            <button type="button" className={planningTab === "history" ? "tab active" : "tab"} onClick={() => setPlanningTab("history")}>История расчётов</button>
                            <button type="button" className={planningTab === "excl" ? "tab active" : "tab"} onClick={() => setPlanningTab("excl")}>Исключения</button>
                        </div>

                        {/* ---- Матрица ---- */}
                        {planningTab === "matrix" ? (
                            <div className="panel">
                                <div className="planning-controls">
                                    <Field label="Режим закупки">
                                        <div className="tabbar" role="group" aria-label="Режим закупки">
                                            <button type="button" className={planningMode === "single" ? "tab active" : "tab"} onClick={() => setPlanningSettings({ ...planningSettings, planning_mode: "single", single_supplier_id: planningSettings.single_supplier_id || "" })}>Один поставщик</button>
                                            <button type="button" className={planningMode === "multi" ? "tab active" : "tab"} onClick={() => setPlanningSettings({ ...planningSettings, planning_mode: "multi", single_supplier_id: "" })}>Несколько поставщиков</button>
                                        </div>
                                    </Field>
                                    {planningIsSingle ? (
                                        <Field label="Поставщик для заказа" style={{ minWidth: "300px" }}>
                                            <select className="inp" value={planningSettings.single_supplier_id || ""} onChange={(e) => setPlanningSettings({ ...planningSettings, single_supplier_id: e.target.value })}>
                                                <option value="">Выберите поставщика</option>
                                                {suppliers.filter((supplier) => supplier.is_active !== false).map((supplier) => (
                                                    <option key={supplier.id} value={supplier.id}>{supplier.display_name || supplier.name || supplier.code}</option>
                                                ))}
                                            </select>
                                        </Field>
                                    ) : (
                                        <Field label="Поставщики для заказа (минимум 2)" style={{ flex: 1 }}>
                                            <div className="supplier-scope-list">
                                                {suppliers.filter((supplier) => supplier.is_active !== false).map((supplier) => (
                                                    <CheckPill
                                                        key={supplier.id}
                                                        checked={(planningSettings.allowed_supplier_ids || []).includes(Number(supplier.id))}
                                                        onChange={(checked) => setPlanningSettings({
                                                            ...planningSettings,
                                                            allowed_supplier_ids: checked
                                                                ? [...new Set([...(planningSettings.allowed_supplier_ids || []), Number(supplier.id)])]
                                                                : (planningSettings.allowed_supplier_ids || []).filter((id) => Number(id) !== Number(supplier.id)),
                                                        })}
                                                    >
                                                        {supplier.display_name || supplier.name || supplier.code}
                                                    </CheckPill>
                                                ))}
                                            </div>
                                        </Field>
                                    )}
                                    <Field label="Бренды" style={{ minWidth: "280px" }}>
                                        <details className="planning-brands"><summary>{(planningSettings.brand_filters || []).join(", ") || "Все бренды"}</summary>
                                            <input className="inp" aria-label="Поиск бренда" placeholder="Поиск бренда" value={brandSearch} onChange={e => setBrandSearch(e.target.value)} />
                                            <button type="button" className="btn" onClick={() => setPlanningSettings({ ...planningSettings, brand_filters: [] })}>Все бренды</button>
                                            {brandsLoading ? <div className="mini">Загрузка брендов…</div> : null}
                                            {brandsError ? <div role="alert">{brandsError}</div> : null}
                                            {!brandsLoading && !brandsError && !availableBrands.length ? <div className="mini">В выбранных прайсах нет доступных брендов</div> : null}
                                            <div style={{ maxHeight: 220, overflowY: "auto" }}>{[...new Set([...availableBrands, ...(planningSettings.brand_filters || [])])].filter(brand => brand.toLowerCase().includes(brandSearch.toLowerCase())).map(brand => <label key={brand} style={{ display: "block" }}><input type="checkbox" checked={(planningSettings.brand_filters || []).includes(brand)} onChange={e => setPlanningSettings({ ...planningSettings, brand_filters: e.target.checked ? [...(planningSettings.brand_filters || []), brand] : planningSettings.brand_filters.filter(value => value !== brand) })} />{brand}</label>)}</div>
                                        </details>
                                        {planningScopeDirty ? <div className="mini">Настройки изменены — пересчитайте закупку</div> : null}
                                    </Field>
                                    <div className="planning-control-actions">
                                        <div className="planning-recalculate-action">
                                            <span className={selectedPlanningRun ? "badge on planning-action-status" : "badge off planning-action-status"}><span className="dot"></span>{selectedPlanningRun?.status || "расчёт не выбран"}</span>
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => recalculatePlanning()} disabled={!selectedPlanningRunId || loading === `planning-recalc:${selectedPlanningRunId}`}>{selectedPlanningRun?.status === "confirmed" ? "Создать новый расчёт" : "Пересчитать"}</button>
                                        </div>
                                        <button type="button" className="btn btn-primary btn-sm planning-create-action" onClick={createPlanning} disabled={loading === "planning-create"}>{loading === "planning-create" ? "Формирование..." : "Сформировать расчёт"}</button>
                                    </div>
                                </div>

                                <details className="settings">
                                    <summary>
                                        <span>⚙ Настройки расчёта</span>
                                        <span className="mini" style={{ fontWeight: 600 }}>свернуто · нажмите чтобы изменить</span>
                                        <span className="chev">▾</span>
                                    </summary>
                                    <div className="settings-body">
                                        <div className="grid4">
                                            <Field label="Активный расчёт">
                                                <select className="inp" value={selectedPlanningRunId} onChange={(e) => setSelectedPlanningRunId(e.target.value)}>
                                                    <option value="">Расчёт не выбран</option>
                                                    {planningRuns.map((run) => (
                                                        <option key={run.id} value={run.id}>#{run.id} · top #{run.top_sales_upload_id} · {run.status}</option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Топ продаж">
                                                <select className="inp" value={planningSettings.top_sales_upload_id || ""} onChange={(e) => setPlanningSettings({ ...planningSettings, top_sales_upload_id: e.target.value })}>
                                                    <option value="">Выберите upload</option>
                                                    {topSalesUploads.filter((upload) => upload.status === "analyzed").map((upload) => (
                                                        <option key={upload.id} value={upload.id}>#{upload.id} {upload.original_filename}</option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Период продаж, дней">
                                                <input className="inp" type="number" min="1" value={planningSettings.sales_period_days} onChange={(e) => setPlanningSettings({ ...planningSettings, sales_period_days: e.target.value })} />
                                            </Field>
                                            <Field label="Целевой запас, дней">
                                                <input className="inp" type="number" min="1" value={planningSettings.target_days} onChange={(e) => setPlanningSettings({ ...planningSettings, target_days: e.target.value })} />
                                            </Field>
                                            <Field label="Макс. строк на экране">
                                                <input className="inp" type="number" min="1" value={planningSettings.top_limit || ""} onChange={(e) => setPlanningSettings({ ...planningSettings, top_limit: e.target.value })} />
                                            </Field>
                                            <Field label="Наша доля от ТОП, %">
                                                <input className="inp" type="number" min="0" max="100" value={planningSettings.top_sales_capture_percent || ""} onChange={(e) => setPlanningSettings({ ...planningSettings, top_sales_capture_percent: e.target.value })} />
                                            </Field>
                                            <Field label="Наценка рынка, %">
                                                <input
                                                    className="inp"
                                                    type="number"
                                                    min="0"
                                                    max="300"
                                                    value={planningSettings.market_markup_percent ?? ""}
                                                    title="с/с рынка = средняя розница топа / (1 + %/100)"
                                                    onChange={(e) => setPlanningSettings({ ...planningSettings, market_markup_percent: e.target.value })}
                                                />
                                            </Field>
                                            <Field label="Мин. продаж, шт">
                                                <input className="inp" value={planningSettings.min_sales_qty || ""} onChange={(e) => setPlanningSettings({ ...planningSettings, min_sales_qty: e.target.value })} />
                                            </Field>
                                            {!planningIsSingle ? <Field label="Ширина названия">
                                                <select className="inp" value={planningNameWidth} onChange={(e) => setPlanningNameWidth(e.target.value)}>
                                                    <option value="260">Уже</option>
                                                    <option value="360">Средняя</option>
                                                    <option value="520">Шире</option>
                                                </select>
                                            </Field>
                                            : null}
                                        </div>
                                        <div className="mini" style={{ marginTop: "8px" }}>
                                            с/с рынка в «Продажи ТОП» = средняя розница топа / (1 + наценка рынка). Строка жёлтая, если цена поставщика выше неё.
                                        </div>
                                        {settingsTopUploadDays && Number(planningSettings.sales_period_days || 0) !== settingsTopUploadDays ? (
                                            <div className="period-warn" role="note">
                                                Топ продаж загружен за {settingsTopUploadDays} дн, а период продаж расчёта — {planningSettings.sales_period_days} дн. Продажи в день будут посчитаны неточно: поставьте период {settingsTopUploadDays} дн.
                                            </div>
                                        ) : null}
                                        <div className="grid3" style={{ marginTop: "12px" }}>
                                            <CheckPill checked={Boolean(planningSettings.include_dropship_stock)} onChange={(next) => setPlanningSettings({ ...planningSettings, include_dropship_stock: next })}>Учитывать остатки dropship</CheckPill>
                                            <CheckPill checked={Boolean(planningSettings.in_stock_only)} onChange={(next) => setPlanningSettings({ ...planningSettings, in_stock_only: next })}>Только товары в наличии у поставщика</CheckPill>
                                            {!planningIsSingle ? <CheckPill checked={Boolean(planningSettings.include_without_wholesale)} onChange={(next) => setPlanningSettings({ ...planningSettings, include_without_wholesale: next })}>Показывать товары без опта</CheckPill> : null}
                                        </div>
                                        <div className="save-bar">
                                            <span className="note">Изменения применяются при формировании / пересчёте</span>
                                            <button type="button" className="btn btn-ghost" onClick={() => recalculatePlanning()} disabled={!selectedPlanningRunId || loading === `planning-recalc:${selectedPlanningRunId}`}>{selectedPlanningRun?.status === "confirmed" ? "Создать новый расчёт" : "Пересчитать текущий"}</button>
                                        </div>
                                    </div>
                                </details>

                                <div className="planning-inline-toolbar">
                                    <span className="planning-inline-metric">SKU <strong>{formatNumber(planningSummary?.total_items || 0)}</strong></span>
                                    <span className="planning-inline-metric warn">Дефицит <strong>{formatNumber(planningSummary?.deficit_items || 0)}</strong></span>
                                    <span className="planning-inline-metric">Реком. <strong>{formatNumber(planningSummary?.total_recommended_qty || 0)}</strong></span>
                                    <span className="planning-inline-metric live">Заказ <strong>{formatNumber(planningDraftTotals.hasDraft ? planningDraftTotals.qty : planningSummary?.total_manual_qty || 0)} · {formatNumber(planningDraftTotals.hasDraft ? planningDraftTotals.amount : planningSummary?.total_manual_amount_uah || 0)} ₴</strong></span>
                                    <span className="planning-inline-metric">Без опта <strong>{formatNumber(planningSummary?.rows_without_wholesale || 0)}</strong></span>
                                    <span className="planning-inline-divider"></span>
                                    <select className="inp inp-sm" value={planningStatus} onChange={(e) => setPlanningStatus(e.target.value)}>
                                        <option value="">Все статусы</option>
                                        <option value="deficit">Дефицит</option>
                                        <option value="enough_stock">Запаса хватает</option>
                                        <option value="no_wholesale">Нет опта</option>
                                    </select>
                                    <input className="inp inp-sm" value={planningQuery} onChange={(e) => setPlanningQuery(e.target.value)} placeholder="SKU, название, производитель" />
                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => loadPlanningItems()}>Фильтр</button>
                                    <button type="button" className="btn btn-ghost btn-sm" onClick={fillPlanningRecommended} disabled={!planningItems.length || planningRunIsFrozen}>Заполнить рекомендуемым</button>
                                    <CheckPill checked={fillSkipExpensive} onChange={setFillSkipExpensive}>кроме дорогих</CheckPill>
                                    {!planningIsSingle ? <button type="button" className="btn btn-ghost btn-sm" onClick={previewPlanningTender} disabled={!selectedPlanningRunId || loading === "planning-preview"}>Предпросмотр</button> : null}
                                    {!planningIsSingle ? <button type="button" className="btn btn-ghost btn-sm" onClick={applyPriceTender} disabled={!selectedPlanningRunId || planningRunIsFrozen || loading === "planning-price-tender"}>Тендер по цене</button> : null}
                                    {!planningIsSingle ? <button type="button" className="btn btn-ghost btn-sm" onClick={applyMinPurchaseTender} disabled={!selectedPlanningRunId || planningRunIsFrozen || loading === "planning-min-tender"}>Тендер по мин. закупке</button> : null}
                                    <span className="planning-inline-grow"></span>
                                    <button type="button" className="btn btn-primary" onClick={formPlanningOrder} disabled={planningScopeDirty || !selectedPlanningRunId || selectedPlanningRun?.status === "confirmed" || loading === "planning-order"}>{loading === "planning-order" ? "Формирование..." : selectedPlanningRun?.status === "confirmed" ? "Заказ сформирован" : "Сформировать заказ →"}</button>
                                </div>

                                <div className="tbl-wrap" style={{ maxHeight: "680px" }}>
                                    <table className="tbl planning-matrix" style={{ minWidth: matrixPlanningIsSingle ? "1450px" : "1660px", tableLayout: "fixed" }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: "105px" }}>Код</th>
                                                <th style={{ width: `${planningNameWidth}px` }}>Наименование</th>
                                                <th style={{ width: "145px" }}>Наш остаток</th>
                                                <th style={{ width: "150px" }}>Продажи<div className="mini" style={{ textTransform: "none" }}>склад / общий</div></th>
                                                <th style={{ width: "150px" }}>Продажи ТОП</th>
                                                <th style={{ width: "95px" }}>Заказать</th>
                                                <th style={{ width: "105px" }}>Запас, дней</th>
                                                <th style={{ width: matrixPlanningIsSingle ? "150px" : "175px" }}>{matrixPlanningIsSingle ? "Цена и остаток" : "Поставщик заказа"}</th>
                                                {!matrixPlanningIsSingle ? <th style={{ width: "125px" }}>Макс. дроп</th> : null}
                                                {!matrixPlanningIsSingle ? <th style={{ width: "190px" }}>Другие прайсы</th> : null}
                                                {matrixPlanningIsSingle ? <th style={{ width: "135px" }}>Дропшип-остаток</th> : null}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {renderMatrixBody()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : null}

                        {/* ---- Заказ ---- */}
                        {planningTab === "order" ? (
                            <div className="panel">
                                <h3>Формирование заказа</h3>
                                <p className="lead">Draft-заказы поставщикам по выбранным количествам из матрицы. Тендер разбивает позиции по минимальной цене с учётом минимальных сумм закупки, после чего каждый заказ можно выгрузить в Excel.</p>

                                {!orderHasResult ? (
                                    <div className="callout"><span className="ic">ⓘ</span><div>Заказ ещё не сформирован. Перейдите на вкладку <b>Матрица</b>, проставьте количества и нажмите <b>«Сформировать заказ»</b>.</div></div>
                                ) : (
                                    <>
                                        {supplierOrderWarnings.length ? (
                                            <div className="callout warn">
                                                <span className="ic">⚠</span>
                                                <div>
                                                    {supplierOrderWarnings.slice(0, 8).map((warning, index) => (<div key={`${warning}-${index}`}>{warning}</div>))}
                                                    {canForceCreateOrders ? (
                                                        <div style={{ marginTop: "8px" }}>
                                                            <button type="button" className="btn btn-ghost btn-sm" onClick={forceFormPlanningOrder} disabled={loading === "planning-order-force"}>Всё равно создать</button>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}

                                        {supplierOrderExportWarnings.length ? (
                                            <div className="callout warn">
                                                <span className="ic">⚠</span>
                                                <div>
                                                    <div>Часть позиций не перенесена в бланк «Рослина карпат»: их коды отсутствуют в шаблоне.</div>
                                                    {supplierOrderExportWarnings.map((line, index) => (
                                                        <div key={`${line.supplier_product_code || "missing"}-${index}`}>
                                                            {line.supplier_product_code || "Код не указан"}: {line.supplier_product_name || "Без названия"} — {formatNumber(line.quantity)} шт.
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}

                                        {allocationSummary ? (
                                            <div className="block">
                                                <div className="bh"><span className="ic">Σ</span><h4>Сводка аллокации</h4></div>
                                                <div className="grid4">
                                                    {[
                                                        ["Кол-во заказа", formatNumber(allocationSummary.total_order_qty)],
                                                        ["Себестоимость", `${formatNumber(allocationSummary.total_order_amount_uah)} грн`],
                                                        ["Идеал по цене", `${formatNumber(allocationSummary.ideal_order_amount_uah)} грн`],
                                                        ["Переплата", `${formatNumber(allocationSummary.overpay_uah)} грн`],
                                                    ].map(([label, value]) => (
                                                        <div key={label} className="fld"><label>{label}</label><div className="val">{value}</div></div>
                                                    ))}
                                                </div>
                                                {(allocationSummary.problems || []).length ? (
                                                    <div className="callout warn" style={{ marginTop: "12px", marginBottom: 0 }}>
                                                        <span className="ic">⚠</span>
                                                        <div>{(allocationSummary.problems || []).slice(0, 5).map((problem) => (<div key={`${problem.item_id}-${problem.status}`}>{problem.sku}: {problem.reason}</div>))}</div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        <div className="block">
                                            <div className="bh"><span className="ic">📦</span><h4>Сформированные заказы поставщикам</h4><span className="hint">{supplierOrders.length} поставщиков</span></div>
                                            <div className="tbl-wrap">
                                                <table className="tbl">
                                                    <thead><tr><th>Поставщик</th><th>Статус</th><th>Строк</th><th>Сумма</th><th>Мин. закупка</th><th>Недобор</th><th>Действия</th></tr></thead>
                                                    <tbody>
                                                        {supplierOrders.map((order) => {
                                                            const isSnimportOrder = ["SNIMPORT", "BELOK"].includes(String(order.supplier_code || "").toUpperCase());
                                                            const cartSync = supplierOrderCartSyncs[order.id];
                                                            return (
                                                            <tr key={order.id}>
                                                                <td>{order.supplier_code}<div className="mini">{order.supplier_name || ""}</div></td>
                                                                <td>{order.status === "prepared" ? "Сформирован" : order.status}</td>
                                                                <td>{order.lines_count}</td>
                                                                <td>{formatNumber(order.total_amount_uah)} грн</td>
                                                                <td>{order.min_purchase_amount_uah ? `${formatNumber(order.min_purchase_amount_uah)} грн` : "—"}</td>
                                                                <td>{formatNumber(order.shortage_amount_uah)} грн</td>
                                                                <td>
                                                                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => downloadSupplierOrder(order)} disabled={loading === `planning-export:${order.id}` || loading === `planning-export-preview:${order.id}`}>Excel</button>
                                                                        {isSnimportOrder ? <button type="button" className="btn btn-primary btn-sm" onClick={() => sendSupplierOrderToSnimportCart(order)} disabled={loading === `snimport-cart:${order.id}`}>{loading === `snimport-cart:${order.id}` ? "Заполняю корзину…" : "В корзину SNIMPORT"}</button> : null}
                                                                        {isSnimportOrder ? <a className="btn btn-ghost btn-sm" href="https://snimport.com.ua/index.php?route=checkout/cart" target="_blank" rel="noreferrer">Открыть корзину</a> : null}
                                                                    </div>
                                                                    {cartSync ? <div className={cartSync.status === "failed" ? "mini trend-up" : "mini"} style={{ marginTop: "6px" }}>
                                                                        Корзина: {cartSync.added_lines_count}/{cartSync.requested_lines_count} строк, {formatNumber(cartSync.added_qty)} шт. ({cartSync.status})
                                                                        {cartSync.error_message ? <div>{cartSync.error_message}</div> : null}
                                                                        {(cartSync.lines || []).filter((line) => line.status !== "added").slice(0, 4).map((line) => <div key={line.id}>{line.supplier_product_code || "Без артикула"}: {line.error_message || line.status}</div>)}
                                                                    </div> : null}
                                                                </td>
                                                            </tr>
                                                            );
                                                        })}
                                                        {!supplierOrders.length ? <tr><td colSpan={7}>Заказы ещё не сформированы.</td></tr> : null}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="save-bar">
                                                <span className="note">Сформированные заказы зафиксированы. Для новых цен создайте новый расчёт.</span>
                                                <button type="button" className="btn btn-ghost" onClick={() => setPlanningTab("matrix")}>← Вернуться в матрицу</button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : null}

                        {/* ---- История расчётов ---- */}
                        {planningTab === "history" ? (
                            <div className="panel">
                                <h3>История расчётов</h3>
                                <p className="lead">Прошлые расчёты закупки. Можно сделать любой активным или пересчитать по текущим ценам и остаткам.</p>
                                <div className="tbl-wrap">
                                    <table className="tbl">
                                        <thead><tr><th>Run</th><th>Основан на</th><th>Top upload</th><th>Статус</th><th>Строки</th><th>Дефицит</th><th>Сумма заказа</th><th>Действия</th></tr></thead>
                                        <tbody>
                                            {planningRuns.map((run) => (
                                                <tr key={run.id} style={{ background: String(run.id) === String(selectedPlanningRunId) ? "var(--sp-accent-soft)" : undefined }}>
                                                    <td><b>#{run.id}</b>{String(run.id) === String(selectedPlanningRunId) ? <span className="pill b" style={{ marginLeft: "6px" }}>активный</span> : null}</td>
                                                    <td>{run.based_on_run_id ? `#${run.based_on_run_id}` : "—"}</td>
                                                    <td>#{run.top_sales_upload_id}</td>
                                                    <td><span className={statusPillClass(run.status)}>{run.status}</span></td>
                                                    <td>{run.total_items}</td>
                                                    <td>{run.deficit_items}</td>
                                                    <td>{formatNumber(run.total_manual_amount_uah)} грн</td>
                                                    <td>
                                                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                                            {String(run.id) === String(selectedPlanningRunId) ? null : <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedPlanningRunId(String(run.id))}>Сделать активным</button>}
                                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => recalculatePlanning(run.id)} disabled={loading === `planning-recalc:${run.id}`}>{run.status === "confirmed" ? "Создать новый" : "Пересчитать"}</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {!planningRuns.length ? <tr><td colSpan={8}>Расчетов пока нет.</td></tr> : null}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : null}

                        {/* ---- Исключения ---- */}
                        {planningTab === "excl" ? (
                            <div className="panel">
                                <h3>Исключения</h3>
                                <p className="lead">Ручные исключения выводят позиции из расчёта закупки — не удаляя их из системы.</p>

                                <div className="callout">
                                    <span className="ic">ⓘ</span>
                                    <div>
                                        <b>Исключения выводят позиции из расчёта закупки.</b>
                                        <ul>
                                            <li><b>Товар (SKU)</b> — товар полностью убирается из матрицы и заказа (снят с продажи, неликвид, сезонка вне сезона, проблемная карточка).</li>
                                            <li><b>Поставщик</b> — его цены и остатки не рассматриваются как источник опта и не участвуют в тендере по цене (нет отгрузок, блокировка, долги, качество).</li>
                                        </ul>
                                        Выключите исключение, чтобы вернуть в расчёт. Изменения применяются при следующем <b>Сформировать / Пересчитать</b>.
                                    </div>
                                </div>

                                <div className="block">
                                    <div className="bh"><span className="ic">＋</span><h4>Добавить исключение</h4></div>
                                    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 1fr 1.4fr auto", gap: "10px", alignItems: "end" }}>
                                        <Field label="Тип">
                                            <select className="inp" value={exclusionDraft.scope_type} onChange={(e) => setExclusionDraft({ ...exclusionDraft, scope_type: e.target.value })}>
                                                <option value="sku">Товар</option>
                                                <option value="supplier">Поставщик</option>
                                            </select>
                                        </Field>
                                        <Field label="SKU">
                                            <input className="inp" value={exclusionDraft.sku} onChange={(e) => setExclusionDraft({ ...exclusionDraft, sku: e.target.value })} disabled={exclusionDraft.scope_type !== "sku"} />
                                        </Field>
                                        <Field label="Код поставщика">
                                            <input className="inp" value={exclusionDraft.supplier_code} onChange={(e) => setExclusionDraft({ ...exclusionDraft, supplier_code: e.target.value })} disabled={exclusionDraft.scope_type !== "supplier"} />
                                        </Field>
                                        <Field label="Комментарий">
                                            <input className="inp" value={exclusionDraft.comment} onChange={(e) => setExclusionDraft({ ...exclusionDraft, comment: e.target.value })} />
                                        </Field>
                                        <button type="button" className="btn btn-primary" onClick={savePlanningExclusion} disabled={loading === "planning-exclusion"}>Исключить</button>
                                    </div>
                                </div>

                                <div className="block">
                                    <div className="bh"><span className="ic">☰</span><h4>Список исключений</h4><span className="hint">{planningExclusions.filter((row) => row.is_active).length} активных</span></div>
                                    <div className="tbl-wrap">
                                        <table className="tbl">
                                            <thead><tr><th>Тип</th><th>Объект</th><th>Комментарий</th><th>Активно</th><th></th></tr></thead>
                                            <tbody>
                                                {planningExclusions.map((row) => (
                                                    <tr key={row.id} style={{ opacity: row.is_active ? 1 : 0.6 }}>
                                                        <td><span className={row.scope_type === "supplier" ? "pill b" : "pill"}>{row.scope_type === "supplier" ? "Поставщик" : "Товар"}</span></td>
                                                        <td><b>{row.scope_type === "supplier" ? (row.supplier_code || `#${row.supplier_id}`) : row.sku}</b></td>
                                                        <td>{row.comment || "—"}</td>
                                                        <td><span className={row.is_active ? "badge on" : "badge off"}><span className="dot"></span>{row.is_active ? "да" : "нет"}</span></td>
                                                        <td>
                                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleExclusion(row, !row.is_active)} disabled={loading === `planning-exclusion-toggle:${row.id}`}>
                                                                {row.is_active ? "Выключить" : "Включить"}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {!planningExclusions.length ? <tr><td colSpan={5}>Исключений пока нет.</td></tr> : null}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="save-bar"><span className="note">Применяется при следующем расчёте / пересчёте</span></div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default PurchasingPage;
